/**
 * Metric Dictionary Constants
 *
 * Defines categories, platforms, and display configuration for metrics.
 * These values align with the backend MetricMetadataDto enums.
 */

export enum MetricCategory {
  REVENUE = "Revenue",
  ENGAGEMENT = "Engagement",
  SESSIONS = "Sessions",
  ADVERTISING = "Advertising",
  BRAND = "Brand",
  SUBSCRIBERS = "Subscribers",
}

export enum MetricPlatform {
  FACEBOOK = "Facebook",
  INSTAGRAM = "Instagram",
  TIKTOK = "TikTok",
  PINTEREST = "Pinterest",
  GOOGLE = "Google",
  YOUTUBE = "YouTube",
  SHOPIFY = "Shopify",
  GA = "GA",
  AMAZON = "Amazon",
  KLAVIYO = "Klaviyo",
  ATTENTIVE = "Attentive",
  MULTI = "Multi",
}

export enum ChannelType {
  ORGANIC = "Organic",
  PAID = "Paid",
  COMBINED = "Combined",
  NA = "N/A",
}

export enum BusinessArea {
  SOCIAL = "Social",
  ECOMMERCE = "Ecommerce",
  SEARCH = "Search",
  SUBSCRIPTIONS = "Subscriptions",
  ANALYTICS = "Analytics",
}

/**
 * Color mapping for metric categories
 * Used for category badges and visual indicators
 */
export const CATEGORY_COLORS: Record<MetricCategory, string> = {
  [MetricCategory.REVENUE]: "#10B981", // Green
  [MetricCategory.ENGAGEMENT]: "#8B5CF6", // Purple
  [MetricCategory.SESSIONS]: "#3B82F6", // Blue
  [MetricCategory.ADVERTISING]: "#F59E0B", // Amber
  [MetricCategory.BRAND]: "#EC4899", // Pink
  [MetricCategory.SUBSCRIBERS]: "#06B6D4", // Cyan
}

/**
 * Background color with transparency for badges
 */
export const CATEGORY_BG_COLORS: Record<MetricCategory, string> = {
  [MetricCategory.REVENUE]: "rgba(16, 185, 129, 0.2)",
  [MetricCategory.ENGAGEMENT]: "rgba(139, 92, 246, 0.2)",
  [MetricCategory.SESSIONS]: "rgba(59, 130, 246, 0.2)",
  [MetricCategory.ADVERTISING]: "rgba(245, 158, 11, 0.2)",
  [MetricCategory.BRAND]: "rgba(236, 72, 153, 0.2)",
  [MetricCategory.SUBSCRIBERS]: "rgba(6, 182, 212, 0.2)",
}

/**
 * Color mapping for channel types
 */
export const CHANNEL_TYPE_COLORS: Record<ChannelType, string> = {
  [ChannelType.ORGANIC]: "#22C55E", // Green
  [ChannelType.PAID]: "#EF4444", // Red
  [ChannelType.COMBINED]: "#A855F7", // Purple
  [ChannelType.NA]: "#6B7280", // Gray
}

/**
 * Platform display names (for cases where enum value differs from display)
 */
export const PLATFORM_DISPLAY_NAMES: Record<MetricPlatform, string> = {
  [MetricPlatform.FACEBOOK]: "Facebook",
  [MetricPlatform.INSTAGRAM]: "Instagram",
  [MetricPlatform.TIKTOK]: "TikTok",
  [MetricPlatform.PINTEREST]: "Pinterest",
  [MetricPlatform.GOOGLE]: "Google",
  [MetricPlatform.YOUTUBE]: "YouTube",
  [MetricPlatform.SHOPIFY]: "Shopify",
  [MetricPlatform.GA]: "Google Analytics",
  [MetricPlatform.AMAZON]: "Amazon",
  [MetricPlatform.KLAVIYO]: "Klaviyo",
  [MetricPlatform.ATTENTIVE]: "Attentive",
  [MetricPlatform.MULTI]: "Multi-Platform",
}

/**
 * Category descriptions for UI help text
 */
export const CATEGORY_DESCRIPTIONS: Record<MetricCategory, string> = {
  [MetricCategory.REVENUE]:
    "Metrics related to sales, orders, and monetary value generated",
  [MetricCategory.ENGAGEMENT]:
    "Metrics measuring audience interaction like likes, comments, shares, and follows",
  [MetricCategory.SESSIONS]:
    "Metrics tracking website visits and user sessions",
  [MetricCategory.ADVERTISING]:
    "Metrics related to paid advertising spend and performance",
  [MetricCategory.BRAND]:
    "Metrics measuring brand value, awareness, and market position",
  [MetricCategory.SUBSCRIBERS]:
    "Metrics tracking email and SMS subscriber growth",
}

/**
 * Type for metric metadata from the API
 */
export type MetricMetadata = {
  key: string
  title: string
  description?: string
  category: MetricCategory[]
  platform: MetricPlatform
  channelType: ChannelType
  businessArea: BusinessArea[]
  format?: string
  relatedMetrics?: string[]
}

/**
 * Type for the metric dictionary API response
 */
export type MetricDictionaryResponse = {
  metrics: MetricMetadata[]
  lastUpdated: string
  totalCount: number
}

/**
 * Type for metric dictionary query filters
 */
export type MetricDictionaryFilters = {
  category?: MetricCategory
  platform?: MetricPlatform
  channelType?: ChannelType
  businessArea?: BusinessArea
  search?: string
}
